import { useCallback, useEffect, useRef } from 'react';

export interface IUseRepeatableFunctionOptions {
  initialDelay?: number;
  repeatInterval?: number;
  enabled?: boolean;
}

export interface IUseRepeatableFunctionReturn {
  start: () => void;
  stop: () => void;
}

/**
 * Hook that enables a function to be called repeatedly while a condition is met.
 * Useful for implementing hold-to-repeat functionality on buttons.
 *
 * @param fn - The function to call repeatedly
 * @param options - Configuration options
 * @param options.initialDelay - Delay before starting repetition (default: 800ms)
 * @param options.repeatInterval - Interval between repetitions (default: 300ms)
 * @param options.enabled - Whether the repeatable functionality is enabled (default: true)
 * @returns Object with start and stop methods
 */
export function useRepeatableFunction(
  fn: () => void,
  options: IUseRepeatableFunctionOptions = {},
): IUseRepeatableFunctionReturn {
  const { initialDelay = 800, repeatInterval = 300, enabled = true } = options;

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Store the latest function in a ref to avoid stale closures
  const fnRef = useRef(fn);
  useEffect(() => {
    fnRef.current = fn;
  }, [fn]);

  const stop = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const start = useCallback(() => {
    if (!enabled) {
      return;
    }

    // Clear any existing timers
    stop();

    // Start the initial delay
    timeoutRef.current = setTimeout(() => {
      // After initial delay, start repeating
      intervalRef.current = setInterval(() => {
        fnRef.current();
      }, repeatInterval);
    }, initialDelay);
  }, [enabled, initialDelay, repeatInterval, stop]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stop();
    };
  }, [stop]);

  return { start, stop };
}
